'use client';

import 'swiper/css';
import 'swiper/css/navigation';
import Image from 'next/image';
import { Button } from '~/components/ui/button';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '~/lib/utils';
import Link from 'next/link';
import { spotlightData } from './data';

const Spotlight = () => {
  return (
    <div className="relative w-full overflow-hidden bg-white p-4 sm:py-20">
      <Swiper
        modules={[Navigation, Autoplay]}
        spaceBetween={20}
        slidesPerView={1}
        navigation={{
          nextEl: '.spotlight-next',
          prevEl: '.spotlight-prev',
        }}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        loop={true}
        speed={800}
        style={{ width: '100%' }}
      >
        {spotlightData.map((slide, index) => (
          <SwiperSlide key={index}>
            <div className="flex items-center justify-center w-full">
              <div
                className="flex items-center justify-between w-full max-w-7xl gap-5 p-10 md:p-20 rounded-3xl md:rounded-[3rem]"
                style={{ backgroundColor: slide.bgColor }}
              >
                <div className="flex flex-col gap-4 sm:gap-6 w-full max-w-[34rem]">
                  <h1 className="text-3xl sm:text-4xl md:text-5xl xl:text-6xl font-bold leading-tight font-secondary">
                    {slide.title}
                  </h1>
                  <p className="text-lg text-shark-600">{slide.description}</p>
                  <div className="flex flex-wrap gap-4 h-full">
                    {slide.buttons.map((button, buttonIndex) => (
                      <Link href={button.href ?? '#'} key={buttonIndex}>
                        <Button
                          variant={button.variant}
                          className={cn({
                            'bg-transparent hover:bg-shark-950/30 hover:text-shark-950':
                              button.variant === 'outline',
                          })}
                        >
                          {button.icon && <button.icon className="h-4 w-4" />}
                          {button.text}
                        </Button>
                      </Link>
                    ))}
                  </div>
                </div>
                <div className="relative hidden lg:block lg:min-w-[400px] lg:min-h-[400px] xl:min-w-[500px] xl:min-h-[500px]">
                  <Image
                    fill
                    src={slide.imageSrc}
                    className="object-cover"
                    alt="Educational Illustration"
                  />
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="hidden lg:flex w-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
        <div className="flex w-full justify-between items-center px-5 xl:px-14">
          <Button
            variant="outline"
            size="icon"
            className="spotlight-prev flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg"
          >
            <ChevronLeft size={24} className="text-shark-950" />
            <span className="sr-only">Previous</span>
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="spotlight-next flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg"
          >
            <ChevronRight size={24} className="text-shark-950" />
            <span className="sr-only">Next</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Spotlight;
